import { useState } from 'react';
import { Card, CardBody, CardTitle, Input, Button, Badge } from 'reactstrap';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useDeliveries, useRedeliver } from './useWebhooks';

const STATUS_OPTIONS = ['', 'pending', 'in_flight', 'succeeded', 'failed', 'dead'];

export function DeliveriesCard({ webhooks }) {
  const [webhookId, setWebhookId] = useState('');
  const [status, setStatus] = useState('');
  const query = useDeliveries({ webhookId, status });
  const redeliver = useRedeliver();

  const rows = query.data?.deliveries ?? [];
  const hasInflight = rows.some((d) => d.status === 'pending' || d.status === 'in_flight');

  return (
    <Card>
      <CardBody className="pb-2">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            <CardTitle tag="h5" className="mb-1">
              Auslieferungen
              {hasInflight && (
                <Badge color="info" pill className="ms-2" style={{ fontWeight: 'normal' }}>
                  Auto-Refresh 10s
                </Badge>
              )}
            </CardTitle>
            <p className="text-muted small mb-0">
              Letzte 100 Deliveries · GET /v1/admin/outbound-webhooks/deliveries
            </p>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Input
              type="select"
              bsSize="sm"
              value={webhookId}
              onChange={(e) => setWebhookId(e.target.value)}
              style={{ maxWidth: 220 }}
            >
              <option value="">Alle Webhooks</option>
              {webhooks.map((w) => (
                <option key={w.id} value={w.id}>
                  #{w.id} · {w.label}
                </option>
              ))}
            </Input>
            <Input
              type="select"
              bsSize="sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ maxWidth: 160 }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s || 'all'} value={s}>
                  {s || 'Alle Status'}
                </option>
              ))}
            </Input>
          </div>
        </div>
      </CardBody>

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!rows.length}
        emptyMessage="Keine Deliveries für die aktuellen Filter."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 80 }}>ID</th>
            <th style={{ width: 80 }}>Webhook</th>
            <th>Event</th>
            <th style={{ width: 110 }}>Status</th>
            <th style={{ width: 80 }}>Versuche</th>
            <th style={{ width: 80 }}>HTTP</th>
            <th>Fehler</th>
            <th style={{ width: 130 }}>Zugestellt</th>
            <th style={{ width: 130 }}>Erstellt</th>
            <th style={{ width: 130 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.id}>
              <td>#{d.id}</td>
              <td>#{d.webhookId}</td>
              <td>
                <code style={{ fontSize: 12 }}>{d.eventType}</code>
                <div className="small text-muted">{d.eventId}</div>
              </td>
              <td>
                <StatusBadge value={d.status} />
              </td>
              <td>{d.attempts}</td>
              <td>{d.httpStatus ?? <span className="text-muted">—</span>}</td>
              <td className="small text-muted">{d.lastError ?? '—'}</td>
              <td>
                {d.deliveredAt ? <RelativeTime iso={d.deliveredAt} /> : <span className="text-muted">—</span>}
              </td>
              <td>
                <RelativeTime iso={d.createdAt} />
              </td>
              <td>
                {(d.status === 'failed' || d.status === 'dead') && (
                  <HasPermission perms={['outboundWebhooks.write']}>
                    <Button
                      size="sm"
                      color="info"
                      outline
                      onClick={() => redeliver.mutate({ id: d.id })}
                      disabled={redeliver.isPending}
                    >
                      ↻ Re-Deliver
                    </Button>
                  </HasPermission>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </Card>
  );
}
